import React, { useMemo } from 'react';
import { Button, Progress, Tag, Tooltip } from 'antd';
import {
  AimOutlined,
  ArrowLeftOutlined,
  BulbOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  CustomerServiceOutlined,
  FlagOutlined,
  InfoCircleOutlined,
  NodeIndexOutlined,
  PieChartOutlined,
  RobotOutlined,
  ScissorOutlined,
  SoundOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { toJalali } from '../../../utils/dateUtils';
import AiAnalysisTree from '../AiAnalysisTree';
import {
  CONVERSATION_STAGES,
  assessmentTone,
  formatSeconds,
  isEmptyValue,
  isPlainObject,
  isSameStage,
  labelFor,
  parseCallAnalysis,
  toFaNumber,
} from './analysisUtils';

const TONE_STYLES = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
  warning: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  error: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800',
  processing: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-800',
  default: 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
};

const ICON_STYLES = {
  purple: 'from-purple-500 to-indigo-600',
  teal: 'from-teal-500 to-emerald-600',
  sky: 'from-sky-500 to-blue-600',
  amber: 'from-amber-500 to-orange-600',
  rose: 'from-rose-500 to-pink-600',
  slate: 'from-slate-500 to-slate-700',
};

const SectionCard = ({ icon, color = 'purple', title, extra, children, className = '' }) => (
  <section className={`rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 shadow-sm p-3 min-w-0 ${className}`}>
    <header className="flex items-center justify-between gap-2 mb-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${ICON_STYLES[color]} text-white flex items-center justify-center shadow-sm shrink-0`}>
          {icon}
        </span>
        <h3 className="custom-font text-sm font-bold text-gray-800 dark:text-gray-100 m-0 truncate">{title}</h3>
      </div>
      {extra}
    </header>
    {children}
  </section>
);

const EmptyNote = ({ children }) => (
  <div className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 leading-6">
    <InfoCircleOutlined className="mt-1 shrink-0" />
    <span>{children}</span>
  </div>
);

const HighlightChip = ({ icon, label, value, tone }) => (
  <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 min-w-0 ${TONE_STYLES[tone]}`}>
    <span className="mt-0.5 shrink-0">{icon}</span>
    <div className="min-w-0">
      <div className="text-[11px] opacity-80">{label}</div>
      <div className="text-sm font-semibold leading-6 break-words">{value}</div>
    </div>
  </div>
);

const KpiTile = ({ label, value, hint, accent }) => (
  <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 px-3 py-2 min-w-0">
    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</div>
    <div className={`text-lg font-bold leading-7 ${accent}`}>{value}</div>
    {hint && <div className="text-[11px] text-gray-400 mt-0.5 truncate">{hint}</div>}
  </div>
);

const InfoGrid = ({ items, columns }) => {
  const filled = items.filter((item) => !isEmptyValue(item.value));
  const empty = items.filter((item) => isEmptyValue(item.value));

  return (
    <div className="space-y-2.5">
      {filled.length > 0 ? (
        <div className={`grid gap-2 ${columns}`}>
          {filled.map((item) => (
            <div key={item.key} className="rounded-lg border border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-900/30 px-3 py-2 min-w-0">
              <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">{item.label}</div>
              <div className="text-sm leading-6 break-words text-gray-800 dark:text-gray-100">{item.value}</div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyNote>اطلاعات مشخصی در این مکالمه مطرح نشد.</EmptyNote>
      )}
      {empty.length > 0 && (
        <div className="text-[11px] text-gray-400 leading-5">
          در مکالمه مطرح نشد: {empty.map((item) => item.label).join('، ')}
        </div>
      )}
    </div>
  );
};

/** مراحل مکالمه به صورت ردیف قرص‌های قابل شکستن (بدون اسکرول افقی) به ترتیب فرایند استاندارد */
const StageTimeline = ({ stages, currentStage }) => {
  const observed = (name) => stages.find((s) => isSameStage(s.name, name));
  const extraStages = stages.filter((s) => !CONVERSATION_STAGES.some((name) => isSameStage(name, s.name)));
  const allStages = [
    ...CONVERSATION_STAGES.map((name) => ({ name, ...observed(name), seen: Boolean(observed(name)) })),
    ...extraStages.map((s) => ({ ...s, seen: true })),
  ];

  return (
    <ol className="flex flex-wrap items-center gap-y-2 gap-x-1 m-0 p-0 list-none">
      {allStages.map((stage, idx) => {
        const current = currentStage && isSameStage(currentStage, stage.name);
        const start = formatSeconds(stage.start);
        const end = formatSeconds(stage.end);
        const time = stage.seen && (start || end) ? `${start || '?'} – ${end || '?'}` : null;

        return (
          <li key={stage.name} className="flex items-center gap-1">
            <Tooltip title={time || stage.note ? (
              <span className="custom-font text-xs">
                {time && <span dir="ltr" className="block">{time}</span>}
                {stage.note}
              </span>
            ) : undefined}
            >
              <span
                className={`custom-font inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] leading-4 whitespace-nowrap ${current
                  ? 'bg-purple-600 border-purple-600 text-white font-bold shadow-sm'
                  : stage.seen
                    ? 'bg-purple-50 border-purple-300 text-purple-700 font-semibold dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-200'
                    : 'bg-white border-dashed border-gray-200 text-gray-400 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-500'}`}
              >
                {stage.seen ? <CheckCircleFilled className="text-[10px]" /> : <span className="text-[10px]">{toFaNumber(idx + 1)}</span>}
                {stage.name}
                {current && <span className="text-[10px] opacity-90">(غالب)</span>}
              </span>
            </Tooltip>
            {idx < allStages.length - 1 && <span className="text-gray-300 dark:text-gray-600 text-xs select-none">‹</span>}
          </li>
        );
      })}
    </ol>
  );
};

const TalkRatio = ({ talk }) => {
  const agent = talk?.agentPercent;
  const customer = talk?.customerPercent;
  if (typeof agent !== 'number' || typeof customer !== 'number') {
    return <EmptyNote>{talk?.basis || 'زمان‌بندی معتبر برای محاسبه نسبت صحبت در دسترس نبود.'}</EmptyNote>;
  }

  const total = agent + customer || 1;
  const agentWidth = (agent / total) * 100;

  return (
    <div className="space-y-3">
      <div className="flex h-9 rounded-xl overflow-hidden text-xs font-bold text-white shadow-inner" dir="rtl">
        <div className="bg-gradient-to-l from-teal-500 to-emerald-500 flex items-center justify-center min-w-[2.5rem]" style={{ width: `${agentWidth}%` }}>
          {toFaNumber(agent, { maximumFractionDigits: 1 })}٪
        </div>
        <div className="bg-gradient-to-l from-sky-500 to-blue-500 flex items-center justify-center min-w-[2.5rem] flex-1">
          {toFaNumber(customer, { maximumFractionDigits: 1 })}٪
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-teal-500" />
          <span className="text-gray-600 dark:text-gray-300">اپراتور</span>
          <span className="text-gray-400 me-auto">{talk.agentSeconds != null ? `${toFaNumber(talk.agentSeconds, { maximumFractionDigits: 1 })} ثانیه` : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-sky-500" />
          <span className="text-gray-600 dark:text-gray-300">مشتری</span>
          <span className="text-gray-400 me-auto">{talk.customerSeconds != null ? `${toFaNumber(talk.customerSeconds, { maximumFractionDigits: 1 })} ثانیه` : ''}</span>
        </div>
      </div>
      {agent > 65 && (
        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded-lg px-3 py-2">
          <WarningOutlined />
          سهم صحبت اپراتور بالاست؛ فرصت بیشتری برای شنیدن مشتری لازم است.
        </div>
      )}
      {talk.basis && <div className="text-[11px] text-gray-400 leading-5">مبنا: {talk.basis}</div>}
    </div>
  );
};

const QuoteBubble = ({ who, text }) => {
  const isAgent = who === 'agent';
  return (
    <div className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[90%] rounded-xl px-3 py-2 text-xs leading-6 ${isAgent
        ? 'bg-teal-50 dark:bg-teal-900/20 rounded-tr-none'
        : 'bg-gray-100 dark:bg-gray-800 rounded-tl-none'}`}
      >
        <span className="font-bold me-1">{isAgent ? 'اپراتور:' : 'مشتری:'}</span>
        «{text}»
      </div>
    </div>
  );
};

const Interruptions = ({ data }) => {
  if (!data) return null;
  if (data.items.length === 0) {
    return (
      <EmptyNote>
        {data.count === 0
          ? 'موردی از قطع صحبت مشتری تشخیص داده نشد.'
          : data.note || 'اطلاعات کافی برای تشخیص قابل‌اعتماد قطع صحبت در دسترس نبود.'}
      </EmptyNote>
    );
  }

  return (
    <div className="space-y-2.5 max-h-[420px] overflow-y-auto pe-1">
      {data.items.map((item, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={idx} className="rounded-xl border border-rose-100 dark:border-rose-900/50 bg-rose-50/40 dark:bg-rose-950/10 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            {formatSeconds(item.time) ? (
              <Tag icon={<ClockCircleOutlined />} className="m-0 font-mono" dir="ltr">{formatSeconds(item.time)}</Tag>
            ) : <span />}
            {typeof item.confidence === 'number' && (
              <Tooltip title="اطمینان مدل">
                <Progress
                  percent={Math.round(item.confidence * 100)}
                  size="small"
                  className="m-0 w-28"
                  strokeColor="#e11d48"
                  format={(p) => <span className="text-[11px]">{toFaNumber(p)}٪</span>}
                />
              </Tooltip>
            )}
          </div>
          {item.customerQuote && <QuoteBubble who="customer" text={item.customerQuote} />}
          {item.agentQuote && <QuoteBubble who="agent" text={item.agentQuote} />}
          {item.reason && <div className="text-xs text-gray-600 dark:text-gray-300 leading-6">{item.reason}</div>}
        </div>
      ))}
    </div>
  );
};

const VoiceDelivery = ({ data, columns }) => {
  if (!data) return null;
  const assessed = data.indicators.filter((i) => !isEmptyValue(i.assessment) || i.score != null);

  if (assessed.length === 0) {
    return <EmptyNote>{data.note || 'صوت یا ویژگی‌های معتبر صوتی برای ارزیابی نحوه بیان در دسترس نبود.'}</EmptyNote>;
  }

  return (
    <div className="space-y-3">
      <div className={`grid gap-2 ${columns}`}>
        {data.indicators.map((indicator) => {
          const tone = assessmentTone(indicator.assessment, indicator.key);
          return (
            <div key={indicator.key} className={`rounded-xl border p-3 min-w-0 ${TONE_STYLES[tone]}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold">{indicator.label}</span>
                <span className="text-xs font-semibold">
                  {isEmptyValue(indicator.assessment) ? 'نامشخص' : indicator.assessment}
                  {indicator.score != null && ` (${toFaNumber(indicator.score)})`}
                </span>
              </div>
              {indicator.evidence && <p className="text-xs leading-6 mt-1.5 mb-0 opacity-90">{indicator.evidence}</p>}
            </div>
          );
        })}
      </div>
      {data.note && <div className="text-[11px] text-gray-400 leading-5">{data.note}</div>}
    </div>
  );
};

const POSITIVE_KEY = /strength|positive|good|قوت|مثبت/i;
const IMPROVE_KEY = /improv|weak|recommend|suggest|coach|risk|issue|بهبود|ضعف|پیشنهاد|ریسک/i;

const BulletList = ({ items, tone }) => (
  <ul className="m-0 p-0 list-none space-y-1.5">
    {items.map((item, idx) => (
      // eslint-disable-next-line react/no-array-index-key
      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-6">
        {tone === 'positive' && <CheckCircleFilled className="text-emerald-500 mt-1.5 shrink-0" />}
        {tone === 'improve' && <BulbOutlined className="text-amber-500 mt-1.5 shrink-0" />}
        {!tone && <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2.5 shrink-0" />}
        <span className="break-words">{item}</span>
      </li>
    ))}
  </ul>
);

const isStringList = (value) => Array.isArray(value) && value.every((v) => typeof v === 'string');

/** بخش‌های تعریف‌نشده در گزارش (مثلاً Coaching): فهرست‌ها و متن‌ها خوانا، بقیه به صورت درختی */
const GenericSection = ({ value, sectionKey }) => {
  if (isStringList(value)) {
    const items = value.filter((v) => !isEmptyValue(v));
    if (items.length === 0) return <EmptyNote>موردی ثبت نشد.</EmptyNote>;
    return <BulletList items={items} tone={POSITIVE_KEY.test(sectionKey) ? 'positive' : (IMPROVE_KEY.test(sectionKey) ? 'improve' : null)} />;
  }

  if (!isPlainObject(value)) {
    if (Array.isArray(value)) return <AiAnalysisTree data={{ [sectionKey]: value }} />;
    return (
      <p className="m-0 text-sm text-gray-700 dark:text-gray-200 leading-7">
        {isEmptyValue(value) ? 'ندارد' : String(value)}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {Object.entries(value).map(([key, child]) => (
        <div key={key}>
          <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">{labelFor(key)}</div>
          {isStringList(child) || !isPlainObject(child)
            ? <GenericSection value={child} sectionKey={key} />
            : <AiAnalysisTree data={child} />}
        </div>
      ))}
    </div>
  );
};

/**
 * گزارش تحلیل هوشمند مکالمه (خروجی JSON سفارشی).
 *
 * @param {'full'|'compact'} variant compact برای مودال‌ها: فقط خلاصه، شاخص‌ها و اطلاعات پایه
 */
const CallAnalysisReport = ({ analysis, call, variant = 'full', onOpenFullReport, action }) => {
  const report = useMemo(() => parseCallAnalysis(analysis), [analysis]);
  if (!report) return null;

  const compact = variant === 'compact';
  const summary = call?.summary || report.summary;
  const observedStages = report.stages.length;
  const interruptionCount = report.interruptions?.count ?? (report.interruptions ? report.interruptions.items.length : null);

  const highlights = [
    report.currentStage && !isEmptyValue(report.currentStage) && { icon: <NodeIndexOutlined />, label: 'مرحله غالب', value: report.currentStage, tone: 'processing' },
    report.highlights.decision && { icon: <FlagOutlined />, label: 'تصمیم مشتری', value: report.highlights.decision, tone: 'success' },
    report.highlights.nextAction && { icon: <AimOutlined />, label: 'اقدام بعدی', value: report.highlights.nextAction, tone: 'warning' },
  ].filter(Boolean);

  return (
    <div className="custom-font space-y-3">
      {/* خلاصه و نکات کلیدی */}
      <div className="rounded-2xl border border-purple-200 dark:border-purple-800/70 bg-gradient-to-br from-white via-purple-50/60 to-indigo-50/40 dark:from-gray-900 dark:via-purple-950/20 dark:to-indigo-950/20 p-3 md:p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center shadow-md">
              <RobotOutlined />
            </span>
            <div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">خلاصه مکالمه</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 flex-wrap">
                {call?.ai_model && <span dir="ltr" className="font-mono">{call.ai_model}</span>}
                {call?.ai_summarized_at && <span>· {toJalali(call.ai_summarized_at, 'jYYYY/jMM/jDD HH:mm')}</span>}
              </div>
            </div>
          </div>
          {action}
        </div>

        {summary && (
          <p className="text-sm leading-7 text-gray-800 dark:text-gray-100 mt-2 mb-0 whitespace-pre-wrap break-words">{summary}</p>
        )}

        {highlights.length > 0 && (
          <div className={`grid gap-2 mt-3 grid-cols-1 ${compact ? 'sm:grid-cols-2' : 'md:grid-cols-3'}`}>
            {highlights.map((h) => <HighlightChip key={h.label} {...h} />)}
          </div>
        )}
      </div>

      {/* شاخص‌های کلیدی */}
      <div>
        <div className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">شاخص‌های کلیدی تماس</div>
        <div className={`grid gap-2.5 grid-cols-2 ${compact ? '' : 'md:grid-cols-4'}`}>
          <KpiTile
            label="سهم صحبت اپراتور"
            value={typeof report.talk?.agentPercent === 'number' ? `${toFaNumber(report.talk.agentPercent, { maximumFractionDigits: 1 })}٪` : '—'}
            hint={typeof report.talk?.customerPercent === 'number' ? `مشتری ${toFaNumber(report.talk.customerPercent, { maximumFractionDigits: 1 })}٪` : 'قابل محاسبه نبود'}
            accent="text-teal-600 dark:text-teal-400"
          />
          <KpiTile
            label="قطع صحبت مشتری"
            value={typeof interruptionCount === 'number' ? toFaNumber(interruptionCount) : '—'}
            hint={typeof interruptionCount === 'number' ? (interruptionCount === 0 ? 'بدون مورد' : 'مورد تأییدشده') : 'قابل تشخیص نبود'}
            accent={interruptionCount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-800 dark:text-gray-100'}
          />
          <KpiTile
            label="مراحل طی‌شده"
            value={`${toFaNumber(observedStages)} از ${toFaNumber(CONVERSATION_STAGES.length)}`}
            hint={report.currentStage && !isEmptyValue(report.currentStage) ? report.currentStage : undefined}
            accent="text-purple-600 dark:text-purple-400"
          />
          <KpiTile
            label="هشدار فرایند فروش"
            value={toFaNumber(report.findings.length)}
            hint={report.findings.length ? 'نیازمند توجه' : 'انحرافی ثبت نشد'}
            accent={report.findings.length ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}
          />
        </div>
      </div>

      {report.info.length > 0 && (
        <SectionCard icon={<CustomerServiceOutlined />} color="teal" title="جزئیات درخواست مشتری">
          <InfoGrid items={report.info} columns={compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'} />
        </SectionCard>
      )}

      {compact ? (
        onOpenFullReport && (
          <Button block icon={<ArrowLeftOutlined />} onClick={onOpenFullReport} className="custom-font">
            مشاهده گزارش کامل تحلیل (مراحل، نحوه بیان، قطع صحبت و ...)
          </Button>
        )
      ) : (
        <>
          <SectionCard icon={<NodeIndexOutlined />} title="مراحل مکالمه">
            {observedStages > 0
              ? <StageTimeline stages={report.stages} currentStage={report.currentStage} />
              : <EmptyNote>مرحله‌ای از فرایند مکالمه قابل تشخیص نبود.</EmptyNote>}
            {report.findings.length > 0 && (
              <div className="mt-4 space-y-2">
                {report.findings.map((finding, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={idx} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2 leading-6">
                    <WarningOutlined className="mt-1.5 shrink-0" />
                    {isPlainObject(finding) ? <AiAnalysisTree data={finding} /> : <span>{String(finding)}</span>}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
            {report.talk && (
              <SectionCard icon={<PieChartOutlined />} color="sky" title="نسبت زمان صحبت">
                <TalkRatio talk={report.talk} />
              </SectionCard>
            )}
            {report.interruptions && (
              <SectionCard
                icon={<ScissorOutlined />}
                color="rose"
                title="قطع صحبت مشتری"
                extra={typeof interruptionCount === 'number' && (
                  <Tag color={interruptionCount > 0 ? 'error' : 'success'} className="m-0">{toFaNumber(interruptionCount)} مورد</Tag>
                )}
              >
                <Interruptions data={report.interruptions} />
              </SectionCard>
            )}
          </div>

          {report.voice && (
            <SectionCard icon={<SoundOutlined />} color="amber" title="نحوه بیان اپراتور">
              <VoiceDelivery data={report.voice} columns="grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" />
            </SectionCard>
          )}

          {report.otherSections.length > 0 && (
            <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
              {report.otherSections.map((section) => (
                <SectionCard
                  key={section.key}
                  icon={/coach|recommend|improv|suggest/i.test(section.key) ? <BulbOutlined /> : <UserOutlined />}
                  color={/coach|recommend|improv|suggest/i.test(section.key) ? 'purple' : 'slate'}
                  title={section.label}
                  className={report.otherSections.length === 1 || (isPlainObject(section.value) && Object.keys(section.value).length > 4) ? 'lg:col-span-2' : ''}
                >
                  <GenericSection value={section.value} sectionKey={section.key} />
                </SectionCard>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CallAnalysisReport;
